import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const set = (ref, values) => {
    if (!values) return;
    Object.keys(values).forEach((attr) => {
        if (!ref || !ref.current) return;
        if (attr === 'className') {
            ref.current.classList.add(values[attr]);
        } else if (ref.current.style[attr] !== values[attr]) {
            // eslint-disable-next-line no-param-reassign
            ref.current.style[attr] = values[attr];
        }
    });
};

export const unset = (ref, values) => {
    if (!values) return;
    Object.keys(values).forEach((attr) => {
        if (!ref || !ref.current) return;
        if (attr === 'className') {
            ref.current.classList.remove(values[attr]);
        }
    });
};

const Transition = ({ isEnterState, classNames, children, states, unmountOnExit, onTransitionEnd }) => {
    const animatorRef = useRef();
    const [prevEnterState, setPrevEnterState] = useState(null);
    const [prevDom, setPrevDom] = useState(animatorRef.current);
    const [render, setRender] = useState(isEnterState || (!isEnterState && !unmountOnExit));
    const isFirstRender = prevEnterState === null;

    const transition = (enter) => {
        unset(animatorRef, { className: enter ? classNames.exit : classNames.enter });
        set(animatorRef, enter ? states.enter : states.exit);
        set(animatorRef, { className: enter ? classNames.enter : classNames.exit });
    };

    // prepare re-enter transition
    useEffect(() => {
        if (animatorRef.current === prevDom) return;
        if (!prevDom) {
            transition(false);
        }
        setPrevDom(animatorRef.current);
    }, [prevDom, animatorRef.current, isFirstRender]); // eslint-disable-line

    // go go state transition
    useEffect(() => {
        if (prevEnterState === isEnterState) return;
        if (isEnterState) setRender(isEnterState);

        if (isFirstRender) {
            transition(isEnterState);
        } else {
            window.setTimeout(() => transition(isEnterState), 0);
        }
        setPrevEnterState(isEnterState);
    }, [prevEnterState, isEnterState, prevDom]); // eslint-disable-line

    const finishAnim = () => {
        if (!isEnterState && unmountOnExit) {
            setRender(false);
        }
        if (onTransitionEnd) {
            onTransitionEnd(isEnterState);
        }
    };

    return (
        render && (
            <div
                ref={animatorRef}
                onTransitionEnd={finishAnim}
                className={isEnterState ? classNames.enter : classNames.exit}
                style={isEnterState ? states.enter : states.exit}
            >
                {children}
            </div>
        )
    );
};

Transition.propTypes = {
    isEnterState: PropTypes.bool,
    unmountOnExit: PropTypes.bool,
    states: PropTypes.shape({
        enter: PropTypes.object,
        exit: PropTypes.object,
    }),
    classNames: PropTypes.shape({
        enter: PropTypes.string,
        exit: PropTypes.string,
    }),
    children: PropTypes.node.isRequired,
    onTransitionEnd: PropTypes.func,
};

Transition.defaultProps = {
    isEnterState: false,
    unmountOnExit: false,
    onTransitionEnd: null,
    classNames: {},
    states: {},
};

export default Transition;
